import {
  AUTOMATION_ACTION_LABELS,
  AUTOMATION_ACTION_TYPES,
  AUTOMATION_CONDITION_FIELD_LABELS,
  AUTOMATION_CONDITION_FIELDS,
  AUTOMATION_FIELD_OPERATORS,
  AUTOMATION_MAX_ACTIONS,
  AUTOMATION_MAX_CONDITIONS,
  AUTOMATION_NOTIFY_RECIPIENT_LABELS,
  AUTOMATION_OPERATOR_LABELS,
  AUTOMATION_TEMPLATE_PLACEHOLDERS,
  AUTOMATION_TRIGGER_DESCRIPTIONS,
  AUTOMATION_TRIGGER_LABELS,
  AUTOMATION_TRIGGER_ONLY_FIELDS,
  AutomationNotifyRecipient,
  AutomationTrigger,
  PermissionCode,
  TICKET_PRIORITY_LABELS,
  TICKET_SOURCE_LABELS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
  automationTriggerSchema,
  isAutomationActionEnabled,
  isAutomationConditionEnabled,
  upsertAutomationRuleRequestSchema,
  type AppModuleCode,
  APP_MODULE_CODES,
  type AutomationAction,
  type AutomationActionType,
  type AutomationCondition,
  type AutomationConditionField,
  type AutomationConditionOperator,
  type AutomationDryRunResponse,
  type AutomationLookups,
  type AutomationRule,
} from '@gb-ticket/shared-types';
import { Loader2Icon, PlusIcon, Trash2Icon } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import {
  useAutomationDryRun,
  useAutomationLookups,
  useAutomationRules,
  useAutomationRuns,
  useCreateAutomationRule,
  useDeleteAutomationRule,
  useSetAutomationRuleActive,
  useUpdateAutomationRule,
} from '@/products/helpdesk/features/automation/use-automation';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

// ---------------------------------------------------------------------------
// Draft model (loosely typed for editing; validated with the shared Zod schema on save)
// ---------------------------------------------------------------------------

type ConditionDraft = {
  field: AutomationConditionField;
  operator: AutomationConditionOperator;
  value: string;
  values: string[];
};

type ActionDraft = {
  type: AutomationActionType;
  userId: string;
  queueId: string;
  keepAssignee: boolean;
  priority: string;
  tag: string;
  slaPolicyId: string;
  recipient: string;
  roleId: string;
  userIds: string[];
  title: string;
  body: string;
};

type RuleForm = {
  name: string;
  description: string;
  isActive: boolean;
  trigger: AutomationTrigger;
  stopProcessing: boolean;
  sortOrder: string;
  conditions: ConditionDraft[];
  actions: ActionDraft[];
};

const MULTI_OPERATORS = new Set<AutomationConditionOperator>(['in', 'not_in']);
const VALUELESS_OPERATORS = new Set<AutomationConditionOperator>(['is_empty', 'is_not_empty']);
const ENUM_OPTIONS: Partial<Record<AutomationConditionField, Record<string, string>>> = {
  type: TICKET_TYPE_LABELS,
  status: TICKET_STATUS_LABELS,
  previousStatus: TICKET_STATUS_LABELS,
  priority: TICKET_PRIORITY_LABELS,
  source: TICKET_SOURCE_LABELS,
};
const REFERENCE_FIELDS = new Set<AutomationConditionField>(['categoryId', 'queueId', 'assigneeId']);
const NUMBER_FIELDS = new Set<AutomationConditionField>(['inactivityMinutes', 'ageMinutes']);

function emptyCondition(field: AutomationConditionField = 'priority'): ConditionDraft {
  return { field, operator: AUTOMATION_FIELD_OPERATORS[field][0] ?? 'eq', value: '', values: [] };
}

function firstActionType(
  enabledModules: readonly AppModuleCode[] | undefined,
): AutomationActionType {
  return (
    AUTOMATION_ACTION_TYPES.find((type) => isAutomationActionEnabled(type, enabledModules)) ??
    'notify'
  );
}

function emptyAction(
  type?: AutomationActionType,
  enabledModules?: readonly AppModuleCode[],
): ActionDraft {
  return {
    type: type ?? firstActionType(enabledModules),
    userId: '',
    queueId: '',
    keepAssignee: false,
    priority: 'HIGH',
    tag: '',
    slaPolicyId: '',
    recipient: AutomationNotifyRecipient.ASSIGNEE,
    roleId: '',
    userIds: [],
    title: 'Ticket #{{ticket.number}}: {{rule.name}}',
    body: '„{{ticket.subject}}“ wurde durch die Regel „{{rule.name}}“ bearbeitet.',
  };
}

function emptyForm(enabledModules?: readonly AppModuleCode[]): RuleForm {
  return {
    name: '',
    description: '',
    isActive: true,
    trigger: AutomationTrigger.TICKET_CREATED,
    stopProcessing: false,
    sortOrder: '100',
    conditions: [],
    actions: [emptyAction(undefined, enabledModules)],
  };
}

function conditionToDraft(condition: AutomationCondition): ConditionDraft {
  const draft = emptyCondition(condition.field);
  draft.operator = condition.operator;
  if ('value' in condition) {
    if (Array.isArray(condition.value)) {
      draft.values = condition.value.map(String);
    } else {
      draft.value = String(condition.value);
    }
  }
  return draft;
}

function actionToDraft(action: AutomationAction): ActionDraft {
  const draft = emptyAction(action.type);
  switch (action.type) {
    case 'assign_user':
      draft.userId = action.userId;
      break;
    case 'assign_queue':
      draft.queueId = action.queueId;
      draft.keepAssignee = action.keepAssignee ?? false;
      break;
    case 'set_priority':
      draft.priority = action.priority;
      break;
    case 'add_tag':
    case 'remove_tag':
      draft.tag = action.tag;
      break;
    case 'set_sla_policy':
      draft.slaPolicyId = action.slaPolicyId;
      break;
    case 'notify':
      draft.recipient = action.recipient;
      draft.roleId = action.roleId ?? '';
      draft.userIds = action.userIds ?? [];
      draft.title = action.title;
      draft.body = action.body;
      break;
  }
  return draft;
}

function ruleToForm(rule: AutomationRule): RuleForm {
  return {
    name: rule.name,
    description: rule.description ?? '',
    isActive: rule.isActive,
    trigger: rule.trigger,
    stopProcessing: rule.stopProcessing,
    sortOrder: String(rule.sortOrder),
    conditions: rule.conditions.map(conditionToDraft),
    actions: rule.actions.map(actionToDraft),
  };
}

/** Converts the loose draft into the wire shape; the Zod schema does the real validation. */
function draftToRequest(form: RuleForm): unknown {
  return {
    name: form.name,
    description: form.description.trim() ? form.description : null,
    isActive: form.isActive,
    trigger: form.trigger,
    stopProcessing: form.stopProcessing,
    sortOrder: Number(form.sortOrder),
    conditions: form.conditions.map((condition) => {
      if (VALUELESS_OPERATORS.has(condition.operator)) {
        return { field: condition.field, operator: condition.operator };
      }
      if (MULTI_OPERATORS.has(condition.operator)) {
        return { field: condition.field, operator: condition.operator, value: condition.values };
      }
      if (NUMBER_FIELDS.has(condition.field)) {
        return {
          field: condition.field,
          operator: condition.operator,
          value: Number(condition.value),
        };
      }
      return { field: condition.field, operator: condition.operator, value: condition.value };
    }),
    actions: form.actions.map((action) => {
      switch (action.type) {
        case 'assign_user':
          return { type: action.type, userId: action.userId };
        case 'assign_queue':
          return { type: action.type, queueId: action.queueId, keepAssignee: action.keepAssignee };
        case 'set_priority':
          return { type: action.type, priority: action.priority };
        case 'add_tag':
        case 'remove_tag':
          return { type: action.type, tag: action.tag };
        case 'set_sla_policy':
          return { type: action.type, slaPolicyId: action.slaPolicyId };
        case 'notify':
          return {
            type: action.type,
            recipient: action.recipient,
            roleId:
              action.recipient === AutomationNotifyRecipient.ROLE
                ? action.roleId || undefined
                : undefined,
            userIds:
              action.recipient === AutomationNotifyRecipient.USERS ? action.userIds : undefined,
            title: action.title,
            body: action.body,
          };
      }
    }),
  };
}

function describeConditionForList(
  condition: AutomationCondition,
  lookups: AutomationLookups | undefined,
): string {
  const field = AUTOMATION_CONDITION_FIELD_LABELS[condition.field];
  const operator = AUTOMATION_OPERATOR_LABELS[condition.operator];
  if (!('value' in condition)) {
    return `${field} ${operator}`;
  }
  const enumLabels = ENUM_OPTIONS[condition.field];
  const nameOf = (value: string): string => {
    if (enumLabels) return enumLabels[value] ?? value;
    if (!lookups) return value;
    return (
      lookups.categories.find((item) => item.id === value)?.name ??
      lookups.queues.find((item) => item.id === value)?.name ??
      lookups.agents.find((item) => item.id === value)?.displayName ??
      value
    );
  };
  const value = Array.isArray(condition.value)
    ? condition.value.map((v) => nameOf(String(v))).join(', ')
    : nameOf(String(condition.value));
  return `${field} ${operator} ${value}`;
}

function describeActionForList(
  action: AutomationAction,
  lookups: AutomationLookups | undefined,
): string {
  const label = AUTOMATION_ACTION_LABELS[action.type];
  switch (action.type) {
    case 'assign_user':
      return `${label}: ${lookups?.agents.find((item) => item.id === action.userId)?.displayName ?? '…'}`;
    case 'assign_queue':
      return `${label}: ${lookups?.queues.find((item) => item.id === action.queueId)?.name ?? '…'}`;
    case 'set_priority':
      return `${label}: ${TICKET_PRIORITY_LABELS[action.priority]}`;
    case 'add_tag':
    case 'remove_tag':
      return `${label}: ${action.tag}`;
    case 'set_sla_policy':
      return `${label}: ${lookups?.slaPolicies.find((item) => item.id === action.slaPolicyId)?.name ?? '…'}`;
    case 'notify':
      return `${label} (${AUTOMATION_NOTIFY_RECIPIENT_LABELS[action.recipient]})`;
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AutomationPage() {
  const me = useMe();
  const canManage = hasPermission(me.data, PermissionCode.AUTOMATION_MANAGE);
  const lookups = useAutomationLookups();
  const rules = useAutomationRules();
  const create = useCreateAutomationRule();
  const update = useUpdateAutomationRule();
  const setActive = useSetAutomationRuleActive();
  const remove = useDeleteAutomationRule();

  const [editing, setEditing] = useState<{ id: string | null; form: RuleForm } | null>(null);
  const [inspecting, setInspecting] = useState<string | null>(null);

  async function onToggle(rule: AutomationRule) {
    try {
      await setActive.mutateAsync({ id: rule.id, isActive: !rule.isActive });
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Änderung fehlgeschlagen.');
    }
  }

  async function onDelete(rule: AutomationRule) {
    try {
      await remove.mutateAsync(rule.id);
      if (editing?.id === rule.id) setEditing(null);
      if (inspecting === rule.id) setInspecting(null);
      toast.success('Regel gelöscht.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.');
    }
  }

  async function onSave(form: RuleForm) {
    const parsed = upsertAutomationRuleRequestSchema.safeParse(draftToRequest(form));
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      toast.error(first ? `${first.message}` : 'Die Regel ist unvollständig.');
      return;
    }
    try {
      if (editing?.id) {
        await update.mutateAsync({ id: editing.id, body: parsed.data });
        toast.success('Regel gespeichert.');
      } else {
        await create.mutateAsync(parsed.data);
        toast.success('Regel angelegt.');
      }
      setEditing(null);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  if (rules.isPending || lookups.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Automatisierungsregeln werden geladen …
      </p>
    );
  }

  if (rules.isError || lookups.isError || !rules.data || !lookups.data) {
    return (
      <section className="space-y-3">
        <h1 className="text-2xl font-bold">Automatisierung</h1>
        <p className="text-muted-foreground">
          {rules.error instanceof ApiRequestError
            ? rules.error.message
            : 'Regeln konnten nicht geladen werden.'}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Automatisierung</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            „Wenn Bedingungen zutreffen, dann Aktionen ausführen.“ Regeln laufen im Hintergrund bei
            Ticket-Ereignissen und alle 5 Minuten zeitgesteuert. Bedingungen werden mit UND
            verknüpft; ohne Bedingung trifft die Regel immer zu.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() =>
              setEditing({ id: null, form: emptyForm(me.data?.enabledModules ?? APP_MODULE_CODES) })
            }
          >
            <PlusIcon aria-hidden /> Neue Regel
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Regeln</CardTitle>
          <CardDescription>
            Sortiert nach Reihenfolge, dann Name. „Stopp“ beendet die Verarbeitung weiterer Regeln.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rules.data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Regeln vorhanden.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Regel</TableHead>
                  <TableHead>Auslöser</TableHead>
                  <TableHead>Wenn</TableHead>
                  <TableHead>Dann</TableHead>
                  <TableHead>Läufe</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.data.items.map((rule) => (
                  <TableRow key={rule.id} className={rule.isActive ? undefined : 'opacity-60'}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{rule.name}</span>
                        <Badge variant={rule.isActive ? 'default' : 'outline'}>
                          {rule.isActive ? 'aktiv' : 'inaktiv'}
                        </Badge>
                        {rule.stopProcessing && <Badge variant="outline">Stopp</Badge>}
                        <span className="text-xs text-muted-foreground">#{rule.sortOrder}</span>
                      </div>
                      {rule.description && (
                        <p className="mt-1 text-xs text-muted-foreground">{rule.description}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {AUTOMATION_TRIGGER_LABELS[rule.trigger]}
                    </TableCell>
                    <TableCell className="text-sm">
                      {rule.conditions.length === 0 ? (
                        <span className="text-muted-foreground">immer</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {rule.conditions.map((condition, index) => (
                            <li key={index}>{describeConditionForList(condition, lookups.data)}</li>
                          ))}
                        </ul>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <ul className="space-y-0.5">
                        {rule.actions.map((action, index) => (
                          <li key={index}>{describeActionForList(action, lookups.data)}</li>
                        ))}
                      </ul>
                    </TableCell>
                    <TableCell className="text-sm">
                      {rule.runCount}
                      {rule.lastRunAt && (
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(rule.lastRunAt)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setInspecting(inspecting === rule.id ? null : rule.id)}
                        >
                          {inspecting === rule.id ? 'Schließen' : 'Testen / Verlauf'}
                        </Button>
                        {canManage && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void onToggle(rule)}
                              disabled={setActive.isPending}
                            >
                              {rule.isActive ? 'Deaktivieren' : 'Aktivieren'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditing({ id: rule.id, form: ruleToForm(rule) })}
                            >
                              Bearbeiten
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-destructive">
                                  Löschen
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Regel „{rule.name}“ löschen?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Die Regel und ihr Ausführungsverlauf werden entfernt. Bereits
                                    ausgeführte Aktionen bleiben bestehen.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => void onDelete(rule)}>
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {inspecting && (
        <RuleInspector
          ruleId={inspecting}
          rule={rules.data.items.find((item) => item.id === inspecting)}
        />
      )}

      {editing && canManage && (
        <RuleEditor
          key={editing.id ?? 'new'}
          initial={editing.form}
          isNew={editing.id === null}
          lookups={lookups.data}
          enabledModules={me.data?.enabledModules ?? APP_MODULE_CODES}
          saving={create.isPending || update.isPending}
          onCancel={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inspector: dry run + recent runs
// ---------------------------------------------------------------------------

function RuleInspector({ ruleId, rule }: { ruleId: string; rule: AutomationRule | undefined }) {
  const runs = useAutomationRuns(ruleId);
  const dryRun = useAutomationDryRun();
  const [ticketRef, setTicketRef] = useState('');
  const [previousStatus, setPreviousStatus] = useState('none');
  const [result, setResult] = useState<AutomationDryRunResponse | null>(null);

  async function onDryRun(event: FormEvent) {
    event.preventDefault();
    try {
      const response = await dryRun.mutateAsync({
        id: ruleId,
        body: {
          ticketRef: ticketRef.trim(),
          previousStatus: previousStatus === 'none' ? undefined : (previousStatus as never),
        },
      });
      setResult(response);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Test fehlgeschlagen.');
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Regel testen{rule ? `: ${rule.name}` : ''}</CardTitle>
          <CardDescription>
            Prüft die Bedingungen gegen ein bestehendes Ticket, ohne Aktionen auszuführen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => void onDryRun(event)}
          >
            <div className="space-y-1">
              <Label htmlFor="dry-run-ref">Ticketnummer</Label>
              <Input
                id="dry-run-ref"
                value={ticketRef}
                onChange={(event) => setTicketRef(event.target.value)}
                placeholder="z. B. 42"
                className="w-32"
              />
            </div>
            {rule?.trigger === AutomationTrigger.TICKET_STATUS_CHANGED && (
              <div className="space-y-1">
                <Label>Vorheriger Status (simuliert)</Label>
                <Select value={previousStatus} onValueChange={setPreviousStatus}>
                  <SelectTrigger className="w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— keiner —</SelectItem>
                    {Object.entries(TICKET_STATUS_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit" disabled={dryRun.isPending || !ticketRef.trim()}>
              {dryRun.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
              Testen
            </Button>
          </form>
          {result && (
            <div className="space-y-2 text-sm">
              <p className="font-medium">
                Ticket #{result.ticketNumber}:{' '}
                {result.matched ? 'Regel trifft zu' : 'Regel trifft nicht zu'}
              </p>
              <ul className="space-y-1">
                {result.conditions.map((condition) => (
                  <li key={condition.index} className="flex items-start gap-2">
                    <Badge variant={condition.matched ? 'default' : 'outline'}>
                      {condition.matched ? 'ja' : 'nein'}
                    </Badge>
                    <span>
                      {condition.label}{' '}
                      <span className="text-muted-foreground">(aktuell: {condition.actual})</span>
                    </span>
                  </li>
                ))}
              </ul>
              {result.plannedActions.length > 0 && (
                <div>
                  <p className="text-muted-foreground">Würde ausführen:</p>
                  <ul className="list-disc pl-5">
                    {result.plannedActions.map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Letzte Ausführungen</CardTitle>
          <CardDescription>Die letzten 100 Läufe dieser Regel.</CardDescription>
        </CardHeader>
        <CardContent>
          {runs.isPending && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden /> Wird geladen …
            </p>
          )}
          {runs.data?.items.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Ausführungen.</p>
          )}
          {runs.data && runs.data.items.length > 0 && (
            <ul className="max-h-80 space-y-2 overflow-auto text-sm">
              {runs.data.items.map((run) => (
                <li key={run.id} className="rounded-md border p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">Ticket #{run.ticketNumber ?? '?'}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(run.firedAt)}
                    </span>
                  </div>
                  <p className={run.succeeded ? undefined : 'text-destructive'}>
                    {run.summary}
                    {run.error ? ` · Fehler: ${run.error}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

function RuleEditor({
  initial,
  isNew,
  lookups,
  enabledModules,
  saving,
  onCancel,
  onSave,
}: {
  initial: RuleForm;
  isNew: boolean;
  lookups: AutomationLookups;
  enabledModules: readonly AppModuleCode[] | undefined;
  saving: boolean;
  onCancel: () => void;
  onSave: (form: RuleForm) => Promise<void>;
}) {
  const [form, setForm] = useState<RuleForm>(initial);

  const availableFields = useMemo(
    () =>
      AUTOMATION_CONDITION_FIELDS.filter((field) => {
        const onlyFor = AUTOMATION_TRIGGER_ONLY_FIELDS[field];
        if (onlyFor && onlyFor !== form.trigger) {
          return false;
        }
        return (
          isAutomationConditionEnabled(field, enabledModules) ||
          form.conditions.some((condition) => condition.field === field)
        );
      }),
    [enabledModules, form.conditions, form.trigger],
  );

  function patch(update: Partial<RuleForm>) {
    setForm((current) => ({ ...current, ...update }));
  }

  function patchCondition(index: number, update: Partial<ConditionDraft>) {
    setForm((current) => ({
      ...current,
      conditions: current.conditions.map((item, i) =>
        i === index ? { ...item, ...update } : item,
      ),
    }));
  }

  function patchAction(index: number, update: Partial<ActionDraft>) {
    setForm((current) => ({
      ...current,
      actions: current.actions.map((item, i) => (i === index ? { ...item, ...update } : item)),
    }));
  }

  function onTriggerChange(value: string) {
    const trigger = automationTriggerSchema.parse(value);
    setForm((current) => ({
      ...current,
      trigger,
      // Drop conditions that are only valid for another trigger.
      conditions: current.conditions.filter((condition) => {
        const onlyFor = AUTOMATION_TRIGGER_ONLY_FIELDS[condition.field];
        return !onlyFor || onlyFor === trigger;
      }),
    }));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isNew ? 'Neue Regel' : 'Regel bearbeiten'}</CardTitle>
        <CardDescription>{AUTOMATION_TRIGGER_DESCRIPTIONS[form.trigger]}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            void onSave(form);
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="rule-name">Name</Label>
              <Input
                id="rule-name"
                value={form.name}
                onChange={(event) => patch({ name: event.target.value })}
                required
                maxLength={120}
              />
            </div>
            <div className="space-y-1">
              <Label>Auslöser</Label>
              <Select value={form.trigger} onValueChange={onTriggerChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(AutomationTrigger).map((trigger) => (
                    <SelectItem key={trigger} value={trigger}>
                      {AUTOMATION_TRIGGER_LABELS[trigger]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="rule-description">Beschreibung (optional)</Label>
              <Input
                id="rule-description"
                value={form.description}
                onChange={(event) => patch({ description: event.target.value })}
                maxLength={500}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-sort">Reihenfolge</Label>
              <Input
                id="rule-sort"
                type="number"
                min={0}
                max={10000}
                value={form.sortOrder}
                onChange={(event) => patch({ sortOrder: event.target.value })}
              />
            </div>
            <div className="flex flex-wrap items-end gap-6 pb-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.isActive}
                  onCheckedChange={(checked) => patch({ isActive: checked === true })}
                />
                Aktiv
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.stopProcessing}
                  onCheckedChange={(checked) => patch({ stopProcessing: checked === true })}
                />
                Nach dieser Regel keine weiteren ausführen
              </label>
            </div>
          </div>

          {/* Conditions */}
          <fieldset className="space-y-3">
            <legend className="font-medium">Wenn (alle Bedingungen treffen zu)</legend>
            {form.conditions.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Keine Bedingung – die Regel trifft bei jedem Ereignis zu.
              </p>
            )}
            {form.conditions.map((condition, index) => (
              <ConditionRow
                key={index}
                condition={condition}
                fields={availableFields}
                lookups={lookups}
                onChange={(update) => patchCondition(index, update)}
                onRemove={() =>
                  patch({ conditions: form.conditions.filter((_, i) => i !== index) })
                }
              />
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={form.conditions.length >= AUTOMATION_MAX_CONDITIONS}
              onClick={() =>
                patch({
                  conditions: [
                    ...form.conditions,
                    emptyCondition(
                      form.trigger === AutomationTrigger.SCHEDULED
                        ? 'inactivityMinutes'
                        : 'priority',
                    ),
                  ],
                })
              }
            >
              <PlusIcon aria-hidden /> Bedingung
            </Button>
          </fieldset>

          {/* Actions */}
          <fieldset className="space-y-3">
            <legend className="font-medium">Dann</legend>
            {form.actions.map((action, index) => (
              <ActionRow
                key={index}
                action={action}
                lookups={lookups}
                enabledModules={enabledModules}
                onChange={(update) => patchAction(index, update)}
                onRemove={
                  form.actions.length > 1
                    ? () => patch({ actions: form.actions.filter((_, i) => i !== index) })
                    : undefined
                }
              />
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={form.actions.length >= AUTOMATION_MAX_ACTIONS}
              onClick={() =>
                patch({ actions: [...form.actions, emptyAction(undefined, enabledModules)] })
              }
            >
              <PlusIcon aria-hidden /> Aktion
            </Button>
          </fieldset>

          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving && <Loader2Icon className="animate-spin" aria-hidden />}
              {isNew ? 'Regel anlegen' : 'Speichern'}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Abbrechen
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ConditionRow({
  condition,
  fields,
  lookups,
  onChange,
  onRemove,
}: {
  condition: ConditionDraft;
  fields: readonly AutomationConditionField[];
  lookups: AutomationLookups;
  onChange: (update: Partial<ConditionDraft>) => void;
  onRemove: () => void;
}) {
  const operators = AUTOMATION_FIELD_OPERATORS[condition.field];
  const multi = MULTI_OPERATORS.has(condition.operator);
  const valueless = VALUELESS_OPERATORS.has(condition.operator);
  const enumOptions = ENUM_OPTIONS[condition.field];
  const referenceOptions: { id: string; label: string }[] | null = REFERENCE_FIELDS.has(
    condition.field,
  )
    ? condition.field === 'categoryId'
      ? lookups.categories.map((item) => ({
          id: item.id,
          label: item.parentId
            ? `${lookups.categories.find((p) => p.id === item.parentId)?.name ?? '…'} / ${item.name}`
            : item.name,
        }))
      : condition.field === 'queueId'
        ? lookups.queues.map((item) => ({ id: item.id, label: item.name }))
        : lookups.agents.map((item) => ({ id: item.id, label: item.displayName }))
    : null;
  const options = enumOptions
    ? Object.entries(enumOptions).map(([id, label]) => ({ id, label }))
    : referenceOptions;

  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border p-3">
      <Select
        value={condition.field}
        onValueChange={(value) => {
          const field = value as AutomationConditionField;
          onChange({
            field,
            operator: AUTOMATION_FIELD_OPERATORS[field][0] ?? 'eq',
            value: '',
            values: [],
          });
        }}
      >
        <SelectTrigger className="w-60">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {fields.map((field) => (
            <SelectItem key={field} value={field}>
              {AUTOMATION_CONDITION_FIELD_LABELS[field]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={condition.operator}
        onValueChange={(value) => onChange({ operator: value as AutomationConditionOperator })}
      >
        <SelectTrigger className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((operator) => (
            <SelectItem key={operator} value={operator}>
              {AUTOMATION_OPERATOR_LABELS[operator]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!valueless && options && !multi && (
        <Select value={condition.value || undefined} onValueChange={(value) => onChange({ value })}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Wert wählen" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {!valueless && options && multi && (
        <div className="flex max-h-40 min-w-64 flex-col gap-1 overflow-auto rounded-md border p-2 text-sm">
          {options.map((option) => (
            <label key={option.id} className="flex items-center gap-2">
              <Checkbox
                checked={condition.values.includes(option.id)}
                onCheckedChange={(checked) =>
                  onChange({
                    values:
                      checked === true
                        ? [...condition.values, option.id]
                        : condition.values.filter((v) => v !== option.id),
                  })
                }
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
      {!valueless && !options && NUMBER_FIELDS.has(condition.field) && (
        <Input
          type="number"
          min={1}
          className="w-32"
          value={condition.value}
          onChange={(event) => onChange({ value: event.target.value })}
          placeholder="Minuten"
        />
      )}
      {!valueless && !options && !NUMBER_FIELDS.has(condition.field) && (
        <Input
          className="w-64"
          value={condition.value}
          onChange={(event) => onChange({ value: event.target.value })}
          placeholder={condition.field === 'tags' ? 'Tag' : 'Text'}
          list={condition.field === 'tags' ? 'automation-tags' : undefined}
        />
      )}
      {condition.field === 'tags' && (
        <datalist id="automation-tags">
          {lookups.tags.map((tag) => (
            <option key={tag} value={tag} />
          ))}
        </datalist>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="Bedingung entfernen"
      >
        <Trash2Icon className="size-4" aria-hidden />
      </Button>
    </div>
  );
}

function ActionRow({
  action,
  lookups,
  enabledModules,
  onChange,
  onRemove,
}: {
  action: ActionDraft;
  lookups: AutomationLookups;
  enabledModules: readonly AppModuleCode[] | undefined;
  onChange: (update: Partial<ActionDraft>) => void;
  onRemove?: () => void;
}) {
  const actionTypes = AUTOMATION_ACTION_TYPES.filter(
    (type) => isAutomationActionEnabled(type, enabledModules) || type === action.type,
  );
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-start gap-2">
        <Select
          value={action.type}
          onValueChange={(value) => onChange({ type: value as AutomationActionType })}
        >
          <SelectTrigger className="w-60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {actionTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {AUTOMATION_ACTION_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {action.type === 'assign_user' && (
          <ReferenceSelect
            value={action.userId}
            options={lookups.agents.map((a) => ({ id: a.id, label: a.displayName }))}
            placeholder="Agent"
            onChange={(userId) => onChange({ userId })}
          />
        )}
        {action.type === 'assign_queue' && (
          <>
            <ReferenceSelect
              value={action.queueId}
              options={lookups.queues.map((q) => ({ id: q.id, label: q.name }))}
              placeholder="Warteschlange"
              onChange={(queueId) => onChange({ queueId })}
            />
            <label className="flex items-center gap-2 pt-2 text-sm">
              <Checkbox
                checked={action.keepAssignee}
                onCheckedChange={(checked) => onChange({ keepAssignee: checked === true })}
              />
              Zugewiesenen Agent behalten
            </label>
          </>
        )}
        {action.type === 'set_priority' && (
          <ReferenceSelect
            value={action.priority}
            options={Object.entries(TICKET_PRIORITY_LABELS).map(([id, label]) => ({ id, label }))}
            placeholder="Priorität"
            onChange={(priority) => onChange({ priority })}
          />
        )}
        {(action.type === 'add_tag' || action.type === 'remove_tag') && (
          <>
            <Input
              className="w-56"
              value={action.tag}
              onChange={(event) => onChange({ tag: event.target.value })}
              placeholder="Tag"
              list="automation-action-tags"
              maxLength={40}
            />
            <datalist id="automation-action-tags">
              {lookups.tags.map((tag) => (
                <option key={tag} value={tag} />
              ))}
            </datalist>
          </>
        )}
        {action.type === 'set_sla_policy' && (
          <ReferenceSelect
            value={action.slaPolicyId}
            options={lookups.slaPolicies.map((p) => ({ id: p.id, label: p.name }))}
            placeholder="SLA-Policy"
            onChange={(slaPolicyId) => onChange({ slaPolicyId })}
          />
        )}
        {action.type === 'notify' && (
          <ReferenceSelect
            value={action.recipient}
            options={Object.values(AutomationNotifyRecipient).map((id) => ({
              id,
              label: AUTOMATION_NOTIFY_RECIPIENT_LABELS[id],
            }))}
            placeholder="Empfänger"
            onChange={(recipient) => onChange({ recipient })}
          />
        )}
        {action.type === 'notify' && action.recipient === AutomationNotifyRecipient.ROLE && (
          <ReferenceSelect
            value={action.roleId}
            options={lookups.roles.map((r) => ({ id: r.id, label: r.name }))}
            placeholder="Rolle"
            onChange={(roleId) => onChange({ roleId })}
          />
        )}
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label="Aktion entfernen"
            className="ml-auto"
          >
            <Trash2Icon className="size-4" aria-hidden />
          </Button>
        )}
      </div>

      {action.type === 'notify' && action.recipient === AutomationNotifyRecipient.USERS && (
        <div className="flex max-h-40 flex-col gap-1 overflow-auto rounded-md border p-2 text-sm">
          {lookups.agents.map((agent) => (
            <label key={agent.id} className="flex items-center gap-2">
              <Checkbox
                checked={action.userIds.includes(agent.id)}
                onCheckedChange={(checked) =>
                  onChange({
                    userIds:
                      checked === true
                        ? [...action.userIds, agent.id]
                        : action.userIds.filter((id) => id !== agent.id),
                  })
                }
              />
              {agent.displayName} <span className="text-muted-foreground">({agent.email})</span>
            </label>
          ))}
        </div>
      )}
      {action.type === 'notify' && (
        <div className="grid gap-2 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Titel</Label>
            <Input
              value={action.title}
              onChange={(event) => onChange({ title: event.target.value })}
              maxLength={140}
            />
          </div>
          <div className="space-y-1">
            <Label>Text</Label>
            <Input
              value={action.body}
              onChange={(event) => onChange({ body: event.target.value })}
              maxLength={1000}
            />
          </div>
          <p className="text-xs text-muted-foreground md:col-span-2">
            Platzhalter: {AUTOMATION_TEMPLATE_PLACEHOLDERS.map((key) => `{{${key}}}`).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

function ReferenceSelect({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string;
  options: { id: string; label: string }[];
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="w-64">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
